import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trash } from '@phosphor-icons/react'
import { useNetworkStatus } from '@/api'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n } from '@/i18n'
import { newId } from '@/lib/ids'
import { enqueueCommand, nextDailyCounter } from '@/offline'
import type { SiteUnit } from '@/offline/sitePack'
import {
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChoiceGroup,
  Field,
  IconButton,
  ScanField,
  useToast,
} from '@/ui'
import type { OpenRunPayload } from '../commands'
import '../commands'
import type { MachineView, ProcessState } from '../projection'
import {
  allergenCleanNeeded,
  bandFor,
  checkMachineScan,
  checkOpenLotBag,
  flushNeeded,
  plannedGrams,
  runCode,
} from '../model/rules'
import { Line, ScanButton, StreamBadge } from '../components/bits'
import { useRefusal } from '../components/useText'
import { stepName } from '../components/labels'
import { ProcessScreen } from '../components/ProcessScreen'
import { useNow } from '../useProcess'

/** F6.1 — Open run: machine label, step, one bag of the lot; the phone decides flush and clean. */
export function OpenRunPage() {
  return <ProcessScreen title="process.open.title">{(state) => <OpenRun state={state} />}</ProcessScreen>
}

interface ScannedLot {
  unit: SiteUnit
  qr_payload: string
}

function OpenRun({ state }: { state: ProcessState }) {
  const { t, locale } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const actor = useActor()
  const navigate = useNavigate()
  const now = useNow()
  const refusal = useRefusal()
  const { online } = useNetworkStatus()
  const [machinePayload, setMachinePayload] = useState<string | null>(null)
  const [machine, setMachine] = useState<MachineView | null>(null)
  const [stepCode, setStepCode] = useState<string | null>(null)
  const [lots, setLots] = useState<ScannedLot[]>([])
  const [saving, setSaving] = useState(false)

  const steps = machine?.step_codes ?? []
  const step = stepCode ?? (steps.length === 1 ? steps[0]! : null)
  const origin = lots[0]?.unit ?? null
  const band =
    machine && step && origin
      ? bandFor(
          state.pack.yield_bands,
          {
            equipment_id: machine.id,
            step_code: step,
            crop_id: origin.crop_id,
            crop_variety_id: origin.crop_variety_id,
          },
          now,
        )
      : null
  const last = machine ? (state.pack.last_runs.find((l) => l.equipment_id === machine.id) ?? null) : null
  const planned = plannedGrams(
    state,
    lots.map((l) => l.unit.id),
  )
  const stream = origin?.stream ?? 'MAIN'
  const flush =
    origin && machine
      ? flushNeeded({ stream, online, shared_use: machine.shared_use, last, origin })
      : false
  const allergen =
    origin && machine
      ? allergenCleanNeeded({ online, shared_use: machine.shared_use, last, crop_id: origin.crop_id })
      : false
  const missingBand = !!(machine && step && origin && !band)
  const mainNeedsSizes = stream === 'MAIN' && band && (band.min_flush_g == null || band.min_input_g == null)
  const belowMin =
    stream === 'MAIN' && band?.min_input_g != null && planned < band.min_input_g ? band.min_input_g : null
  const ready = !!(machine && step && origin && band && !mainNeedsSizes && belowMin === null)

  const open = async () => {
    if (!ready || !machine || !step || !origin || !band || !machinePayload) return
    setSaving(true)
    try {
      const runId = newId()
      const n = await nextDailyCounter(`run:${machine.code}`)
      const payload: OpenRunPayload = {
        run_id: runId,
        code_proposed: runCode(machine.code, n),
        equipment_id: machine.id,
        equipment_qr_payload: machinePayload,
        step_code: step,
        lot_bag_qr_payloads: lots.map((l) => l.qr_payload),
        lot_unit_ids: lots.map((l) => l.unit.id),
        yield_band_id: band.id,
        flush_required: flush,
        allergen_clean_required: allergen,
        phone_online: online,
        previous_run_id: last?.run_id ?? null,
        planned_g: planned,
      }
      await enqueueCommand({ id: runId, type: 'open_run', payload, actorId: actor.user_id })
      toast.success(t('process.open.done', { code: payload.code_proposed }))
      navigate(`/m/process/run/${runId}`, { replace: true })
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Field label={<BilingualLabel k="process.open.machine" />} required>
        <ScanField
          value={machinePayload}
          sheetTitle={<BilingualLabel k="process.open.machine" />}
          validate={(c) => {
            const v = checkMachineScan(state, c)
            return v.ok ? null : refusal(v.reason, { code: v.detail ?? '' })
          }}
          onChange={(c) => {
            setMachinePayload(c)
            const v = c ? checkMachineScan(state, c) : null
            setMachine(v?.ok ? v.value : null)
            setStepCode(null)
            setLots([])
          }}
          detail={machine ? `${machine.code} · ${machine.name}` : undefined}
        />
      </Field>

      {machine && steps.length > 1 && (
        <Field label={<BilingualLabel k="process.open.step" />} required>
          <ChoiceGroup
            options={steps.map((s) => ({ value: s, label: stepName(state, s, locale) }))}
            value={step}
            onChange={setStepCode}
          />
        </Field>
      )}
      {machine && steps.length === 1 && (
        <Line label={t('process.open.step')} value={stepName(state, steps[0]!, locale)} />
      )}

      {machine && step && (
        <>
          <ScanButton
            variant={lots.length ? 'secondary' : 'primary'}
            continuous={false}
            label={<BilingualLabel k={lots.length ? 'process.open.addLot' : 'process.open.scanLot'} />}
            validate={(c) => {
              const v = checkOpenLotBag(
                state,
                c,
                origin,
                lots.map((l) => l.unit.id),
              )
              return v.ok ? null : refusal(v.reason)
            }}
            onScan={(c) => {
              const v = checkOpenLotBag(
                state,
                c,
                origin,
                lots.map((l) => l.unit.id),
              )
              if (v.ok) setLots((ls) => [...ls, { unit: v.value, qr_payload: c.trim().toUpperCase() }])
            }}
          />
          {lots.length > 0 && (
            <Card>
              <CardHeader eyebrow={t('process.open.lots')} />
              <CardBody className="py-1">
                <ul>
                  {lots.map((l, i) => (
                    <li key={l.unit.id} className="flex items-center gap-3 border-b border-rule/70 py-2 last:border-b-0">
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-[15px] font-medium">{l.unit.code}</div>
                        <div className="text-[12px] text-muted">
                          {[l.unit.farmer_code, l.unit.crop_name, l.unit.season_code].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <span className="font-mono text-[13px] tabular">{fmt.weight(plannedGrams(state, [l.unit.id]))}</span>
                      {i > 0 || lots.length === 1 ? (
                        <IconButton
                          size="touch"
                          label={t('process.remove')}
                          icon={<Trash />}
                          onClick={() => setLots((ls) => ls.filter((x) => x.unit.id !== l.unit.id))}
                        />
                      ) : null}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}
        </>
      )}

      {origin && machine && step && (
        <Card>
          <CardHeader eyebrow={t('process.open.check')} />
          <CardBody className="py-1">
            <Line label={t('process.open.runType')} value={<StreamBadge stream={stream} />} />
            <Line
              label={t('process.open.planned')}
              value={
                band?.min_input_g != null && stream === 'MAIN'
                  ? t('process.open.plannedOfMin', { kg: fmt.weight(planned), min: fmt.weight(band.min_input_g) })
                  : fmt.weight(planned)
              }
              tone={belowMin !== null ? 'warn' : undefined}
            />
            <Line
              label={t('process.open.previous')}
              value={
                machine.shared_use || !last
                  ? t('process.open.previousUnknown')
                  : [last.code, last.crop_name, last.farmer_code, last.at ? fmt.dateTime(last.at) : null]
                      .filter(Boolean)
                      .join(' · ')
              }
            />
            <Line
              label={t('process.open.flush')}
              value={
                flush
                  ? t('process.open.flushNeeded', { kg: fmt.weight(band?.min_flush_g ?? 0) })
                  : t('process.open.notNeeded')
              }
              tone={flush ? 'warn' : 'ok'}
            />
            <Line
              label={t('process.open.allergen')}
              value={allergen ? t('process.open.allergenNeeded') : t('process.open.notNeeded')}
              tone={allergen ? 'warn' : 'ok'}
            />
          </CardBody>
        </Card>
      )}

      {missingBand && <Banner tone="error">{refusal('NO_SETTINGS')}</Banner>}
      {mainNeedsSizes && <Banner tone="error">{refusal('NO_SETTINGS')}</Banner>}
      {belowMin !== null && (
        <Banner tone="warn" title={t('process.open.belowMinTitle')}>
          {t('process.open.belowMinBody', { min: fmt.weight(belowMin) })}
        </Banner>
      )}
      {origin && !online && stream === 'MAIN' && <Banner tone="info">{t('process.open.offlineNote')}</Banner>}

      <Button size="xl" fullWidth disabled={!ready} loading={saving} onClick={() => void open()}>
        <BilingualLabel k="process.open.title" />
      </Button>
      <Button variant="ghost" size="touch" fullWidth onClick={() => navigate('/m/process')}>
        <BilingualLabel k="common.cancel" />
      </Button>
    </div>
  )
}
